import { useCallback, useEffect, useMemo, useState } from 'react';
import { api, parseErr } from '@/lib/api';
import { toast } from 'sonner';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Textarea } from '@/components/ui/textarea';
import {
  Dialog, DialogContent, DialogHeader, DialogTitle,
  DialogFooter, DialogDescription,
} from '@/components/ui/dialog';
import {
  ClipboardList, ClipboardCheck, Wallet, Plus, AlertTriangle, Clock, IndianRupee,
  PhoneCall, CheckCircle2,
} from 'lucide-react';
import { useAuth } from '@/lib/auth';
import { useAutoRefetch } from '@/lib/useAutoRefetch';
import { fmtDate } from '@/lib/format';
import { SupportingEvidence } from '@/components/followups/SupportingEvidence';

const SUB_TABS = [
  { id: 'actions',     label: 'Actions',       icon: ClipboardList },
  { id: 'approvals',   label: 'Approvals',     icon: ClipboardCheck },
  { id: 'collections', label: 'Collections',   icon: Wallet },
];

const STATUS_LABELS = {
  actions: {
    pending: 'Pending', in_progress: 'In progress', done: 'Done',
    blocked: 'Blocked', waiting_for_client: 'Waiting client', need_help: 'Need help',
  },
  approvals: {
    awaiting: 'Awaiting', approved: 'Approved', changes_requested: 'Changes requested',
    rejected: 'Rejected', payment_promised: 'Payment promised',
    payment_received: 'Payment received', no_response: 'No response',
  },
  collections: {
    not_sent: 'Not sent', sent: 'Sent', promised: 'Promised',
    partially_paid: 'Partial', paid: 'Paid', overdue: 'Overdue',
  },
};
const STATUS_TINT = {
  pending: 'chip', in_progress: 'chip chip-info', done: 'chip chip-success',
  blocked: 'chip chip-danger', waiting_for_client: 'chip chip-warning',
  need_help: 'chip chip-warning',
  awaiting: 'chip chip-info', approved: 'chip chip-success',
  changes_requested: 'chip chip-warning', rejected: 'chip chip-danger',
  payment_promised: 'chip chip-info', payment_received: 'chip chip-success',
  no_response: 'chip chip-warning',
  not_sent: 'chip', sent: 'chip chip-info', promised: 'chip chip-info',
  partially_paid: 'chip chip-warning', paid: 'chip chip-success',
  overdue: 'chip chip-danger',
};
const fmtINR = (n) => (n == null || n === '') ? '—' : `₹ ${Number(n).toLocaleString('en-IN')}`;

const APPROVAL_CREATE_ROLES = ['owner', 'accounts', 'operations', 'dm_creative', 'dm_technical', 'dm_architecture'];
const COLLECTION_CREATE_ROLES = ['owner', 'accounts', 'dm_creative', 'dm_technical', 'dm_architecture'];
const ACTION_CREATE_ROLES = ['owner', 'operations', 'dm_creative', 'dm_technical', 'dm_architecture'];

export function ProjectFollowupsPanel({ projectId }) {
  const { user } = useAuth();
  const [sub, setSub] = useState('actions');
  const [actions, setActions] = useState([]);
  const [approvals, setApprovals] = useState([]);
  const [collections, setCollections] = useState([]);
  const [users, setUsers] = useState([]);
  const [busy, setBusy] = useState(false);
  const [openCreate, setOpenCreate] = useState(false);
  const [logRow, setLogRow] = useState(null);   // {kind, row}

  const load = useCallback(async () => {
    setBusy(true);
    try {
      const [a, ap, c] = await Promise.all([
        api.get(`/projects/${projectId}/actions`),
        api.get(`/projects/${projectId}/client-approvals`),
        api.get(`/projects/${projectId}/collections`),
      ]);
      setActions(a.data.rows || []);
      setApprovals(ap.data.rows || []);
      setCollections(c.data.rows || []);
    } catch (e) { toast.error(parseErr(e)); }
    finally { setBusy(false); }
  }, [projectId]);

  useEffect(() => {
    api.get('/users').then(r => setUsers(r.data || [])).catch(() => setUsers([]));
    load();
  }, [load]);

  useAutoRefetch(load,
    { collections: ['actions', 'client_approvals', 'collections_ledger'] });

  const usersById = useMemo(
    () => Object.fromEntries((users || []).map(u => [u.id, u])),
    [users],
  );
  const userCell = (uid) => usersById[uid]?.name ||
    <span className="text-[hsl(var(--foreground-subtle))]">—</span>;

  const canCreate = (
    (sub === 'actions'     && ACTION_CREATE_ROLES.includes(user?.role)) ||
    (sub === 'approvals'   && APPROVAL_CREATE_ROLES.includes(user?.role)) ||
    (sub === 'collections' && COLLECTION_CREATE_ROLES.includes(user?.role))
  );

  const kpi = {
    open_actions: actions.filter(a => !['done'].includes(a.status)).length,
    overdue_actions: actions.filter(a => a.is_overdue).length,
    overdue_approvals: approvals.filter(a => a.is_overdue).length,
    outstanding_amount: collections.reduce((s, c) => s + Number(c.balance || 0), 0),
    overdue_collections: collections.filter(c => c.is_overdue).length,
  };

  return (
    <div className="mt-3" data-testid="project-followups-panel">
      {/* KPI mini strip */}
      <div className="grid grid-cols-2 sm:grid-cols-5 gap-2 mb-3" data-testid="pf-kpi">
        <MiniStat label="Open actions"  value={kpi.open_actions}      tint="info" />
        <MiniStat label="Overdue actions" value={kpi.overdue_actions} tint="danger" />
        <MiniStat label="Overdue approvals" value={kpi.overdue_approvals} tint="warning" />
        <MiniStat label="Outstanding" value={fmtINR(kpi.outstanding_amount)} tint="warning" />
        <MiniStat label="Overdue PIs" value={kpi.overdue_collections}  tint="danger" />
      </div>

      {/* Sub-tabs */}
      <div className="flex items-center justify-between mb-3 border-b"
           style={{ borderColor: 'hsl(var(--border))' }}>
        <div className="flex items-center gap-1">
          {SUB_TABS.map(t => {
            const Icon = t.icon;
            const active = sub === t.id;
            const count = t.id === 'actions' ? actions.length
                        : t.id === 'approvals' ? approvals.length
                        : collections.length;
            return (
              <button key={t.id} onClick={() => setSub(t.id)}
                      data-testid={`pf-subtab-${t.id}`}
                      className={`inline-flex items-center gap-1.5 px-3 py-2 text-[13px] border-b-2 transition-colors ${
                        active
                          ? 'border-[hsl(var(--primary))] text-[hsl(var(--primary))] font-semibold'
                          : 'border-transparent text-[hsl(var(--foreground-muted))] hover:text-[hsl(var(--foreground))]'
                      }`}>
                <Icon size={13} />{t.label}
                <span className="text-[10.5px] px-1.5 py-[1px] rounded-full bg-[hsl(var(--surface-1))] text-[hsl(var(--foreground-muted))]">
                  {count}
                </span>
              </button>
            );
          })}
        </div>
        {canCreate && (
          <Button size="sm" onClick={() => setOpenCreate(true)}
                  data-testid={`pf-create-${sub}`}>
            <Plus size={13} className="mr-1" />New {sub === 'collections' ? 'PI' : sub.slice(0, -1)}
          </Button>
        )}
      </div>

      {busy && <div className="py-8 text-center text-[hsl(var(--foreground-muted))]">Loading…</div>}

      {!busy && sub === 'actions' && (
        <ActionsTable rows={actions} userCell={userCell}
                      onLog={(r) => setLogRow({ kind: 'actions', row: r })} />
      )}
      {!busy && sub === 'approvals' && (
        <ApprovalsTable rows={approvals} userCell={userCell} user={user}
                        onLog={(r) => setLogRow({ kind: 'approvals', row: r })}
                        onChanged={load} />
      )}
      {!busy && sub === 'collections' && (
        <CollectionsTable rows={collections} userCell={userCell} user={user}
                          onLog={(r) => setLogRow({ kind: 'collections', row: r })}
                          onChanged={load} />
      )}

      {openCreate && (
        <CreateDialog kind={sub} projectId={projectId} users={users}
                      onClose={() => setOpenCreate(false)}
                      onDone={() => { setOpenCreate(false); load(); }} />
      )}
      {logRow && (
        <LogDialog {...logRow} onClose={() => setLogRow(null)}
                   onDone={() => { setLogRow(null); load(); }} />
      )}
    </div>
  );
}

function MiniStat({ label, value, tint }) {
  const tintClass = {
    info: 'bg-[hsl(210_90%_96%)] text-[hsl(210_60%_30%)] border-[hsl(210_70%_86%)]',
    warning: 'bg-[hsl(48_96%_94%)] text-[hsl(38_60%_28%)] border-[hsl(48_80%_75%)]',
    danger: 'bg-[hsl(0_90%_96%)] text-[hsl(0_60%_35%)] border-[hsl(0_70%_82%)]',
    success: 'bg-[hsl(142_70%_94%)] text-[hsl(142_50%_25%)] border-[hsl(142_60%_78%)]',
  }[tint] || 'bg-[hsl(var(--surface-1))] border-[hsl(var(--border))]';
  return (
    <div className={`rounded-md border px-2.5 py-1.5 ${tintClass}`}>
      <div className="text-[10.5px] uppercase tracking-wide opacity-80">{label}</div>
      <div className="text-[14px] font-semibold tabular-nums truncate">{value}</div>
    </div>
  );
}

function EmptyRow({ colSpan, msg }) {
  return (
    <tr>
      <td colSpan={colSpan} className="text-center py-6 text-[13px] text-[hsl(var(--foreground-muted))]">
        {msg}
      </td>
    </tr>
  );
}

function ActionsTable({ rows, userCell, onLog }) {
  return (
    <div className="border rounded-xl bg-[hsl(var(--card))] overflow-x-auto"
         style={{ borderColor: 'hsl(var(--border))' }}>
      <table className="tbl" data-testid="pf-actions-table">
        <thead><tr>
          <th>Action</th><th>Owner</th><th>Due</th><th>Status</th>
          <th>Last update</th><th>Blocker</th><th></th>
        </tr></thead>
        <tbody>
          {rows.length === 0 && <EmptyRow colSpan={7} msg="No actions yet. Log a commitment to get started." />}
          {rows.map(r => (
            <tr key={r.id} data-testid={`pf-action-${r.id}`}
                data-overdue={r.is_overdue ? 'true' : 'false'}
                style={r.is_overdue ? { boxShadow: 'inset 3px 0 0 hsl(var(--danger))' } :
                       r.is_stale ? { boxShadow: 'inset 3px 0 0 hsl(48 90% 55%)' } : undefined}>
              <td className="font-medium">{r.title}</td>
              <td className="text-[12.5px]">{userCell(r.owner_id)}</td>
              <td className="text-[12px]">
                {r.due_date ? fmtDate(r.due_date) : <span className="text-[hsl(var(--foreground-subtle))]">—</span>}
                {r.is_overdue && <span className="chip chip-danger text-[10px] ml-1">OVERDUE</span>}
                {r.is_stale && !r.is_overdue && <span className="chip chip-warning text-[10px] ml-1">STALE</span>}
              </td>
              <td><span className={STATUS_TINT[r.status]}>{STATUS_LABELS.actions[r.status]}</span></td>
              <td className="text-[11.5px] text-[hsl(var(--foreground-muted))]">
                {r.last_update_at ? fmtDate(r.last_update_at) : '—'}
              </td>
              <td className="text-[11.5px] text-[hsl(var(--foreground-muted))] max-w-[160px] truncate">
                {r.blocker || '—'}
              </td>
              <td className="text-right">
                <Button size="sm" variant="outline" onClick={() => onLog(r)}
                        data-testid={`pf-action-log-${r.id}`}>Log</Button>
              </td>
            </tr>
          ))}
        </tbody>
      </table>
    </div>
  );
}

function ApprovalsTable({ rows, userCell, user, onLog, onChanged }) {
  const isOwner = user?.role === 'owner';
  return (
    <div className="border rounded-xl bg-[hsl(var(--card))] overflow-x-auto"
         style={{ borderColor: 'hsl(var(--border))' }}>
      <table className="tbl" data-testid="pf-approvals-table">
        <thead><tr>
          <th>Item</th><th>Type</th><th>Amount</th><th>Sent</th>
          <th>Expected reply</th><th>Owner</th><th>Status</th><th>Evidence</th><th></th>
        </tr></thead>
        <tbody>
          {rows.length === 0 && <EmptyRow colSpan={9} msg="No client approvals in flight for this project." />}
          {rows.map(r => (
            <tr key={r.id} data-testid={`pf-approval-${r.id}`}
                data-overdue={r.is_overdue ? 'true' : 'false'}
                style={r.is_overdue ? { boxShadow: 'inset 3px 0 0 hsl(0 70% 55%)', background: 'hsl(0 90% 98%)' } : undefined}>
              <td className="font-medium">
                <div>{r.item_title}</div>
                <div className="text-[11px] text-[hsl(var(--foreground-subtle))]">{r.client_contact_name}</div>
              </td>
              <td className="text-[12px]"><span className="chip">{r.item_type}</span></td>
              <td className="text-[12.5px] tabular-nums">{fmtINR(r.amount)}</td>
              <td className="text-[12px]">{r.sent_date ? fmtDate(r.sent_date) : '—'}</td>
              <td className="text-[12px]">
                {r.expected_response_date ? fmtDate(r.expected_response_date) : '—'}
                {r.is_overdue && <span className="chip chip-danger text-[10px] ml-1">OVERDUE</span>}
              </td>
              <td className="text-[12.5px]">{userCell(r.responsible_user_id)}</td>
              <td><span className={STATUS_TINT[r.status]}>{STATUS_LABELS.approvals[r.status]}</span></td>
              <td>
                <SupportingEvidence
                  entity="client-approvals" parentId={r.id} items={r.attachments}
                  canEdit={APPROVAL_CREATE_ROLES.includes(user?.role)}
                  currentUserEmail={user?.email} isOwner={isOwner}
                  onChange={onChanged}
                />
              </td>
              <td className="text-right">
                <Button size="sm" variant="outline" onClick={() => onLog(r)}
                        data-testid={`pf-approval-log-${r.id}`}>Log</Button>
              </td>
            </tr>
          ))}
        </tbody>
      </table>
    </div>
  );
}

function CollectionsTable({ rows, userCell, user, onLog, onChanged }) {
  const isOwner = user?.role === 'owner';
  return (
    <div className="border rounded-xl bg-[hsl(var(--card))] overflow-x-auto"
         style={{ borderColor: 'hsl(var(--border))' }}>
      <table className="tbl" data-testid="pf-collections-table">
        <thead><tr>
          <th>PI</th><th>Total</th><th>Received</th><th>Balance</th><th>Due</th>
          <th>Next follow-up</th><th>Owner</th><th>Status</th><th>Evidence</th><th></th>
        </tr></thead>
        <tbody>
          {rows.length === 0 && <EmptyRow colSpan={10} msg="No PIs / collections logged for this project." />}
          {rows.map(r => (
            <tr key={r.id} data-testid={`pf-collection-${r.id}`}
                data-overdue={r.is_overdue ? 'true' : 'false'}
                style={r.is_overdue ? { boxShadow: 'inset 3px 0 0 hsl(0 70% 55%)', background: 'hsl(0 90% 98%)' } : undefined}>
              <td className="font-medium font-mono text-[12px]">{r.pi_number}</td>
              <td className="tabular-nums">{fmtINR(r.total_amount)}</td>
              <td className="tabular-nums text-[hsl(var(--success))]">{fmtINR(r.amount_received)}</td>
              <td className="tabular-nums font-semibold">{fmtINR(r.balance)}</td>
              <td className="text-[12px]">
                {r.due_date ? fmtDate(r.due_date) : '—'}
                {r.is_overdue && <span className="chip chip-danger text-[10px] ml-1">OVERDUE</span>}
              </td>
              <td className="text-[11.5px] text-[hsl(var(--foreground-muted))]">
                {r.next_followup_date ? fmtDate(r.next_followup_date) : '—'}
              </td>
              <td className="text-[12.5px]">{userCell(r.responsible_accounts_user_id)}</td>
              <td><span className={STATUS_TINT[r.status]}>{STATUS_LABELS.collections[r.status]}</span></td>
              <td>
                <SupportingEvidence
                  entity="collections" parentId={r.id} items={r.attachments}
                  canEdit={COLLECTION_CREATE_ROLES.includes(user?.role)}
                  currentUserEmail={user?.email} isOwner={isOwner}
                  onChange={onChanged}
                />
              </td>
              <td className="text-right whitespace-nowrap">
                <Button size="sm" variant="outline" onClick={() => onLog(r)}
                        data-testid={`pf-collection-log-${r.id}`}>Log</Button>
              </td>
            </tr>
          ))}
        </tbody>
      </table>
    </div>
  );
}

// -------- Create dialogs (Action / Approval / Collection) --------

function CreateDialog({ kind, projectId, users, onClose, onDone }) {
  const [busy, setBusy] = useState(false);
  const [form, setForm] = useState(
    kind === 'actions' ? { title: '', owner_id: '', due_date: '', status: 'pending', blocker: '' } :
    kind === 'approvals' ? {
      item_type: 'deliverable', item_title: '', client_contact_name: '',
      amount: '', sent_date: '', expected_response_date: '',
      responsible_user_id: '', status: 'awaiting',
    } :
    { pi_number: '', total_amount: '', amount_received: '', due_date: '',
      next_followup_date: '', responsible_accounts_user_id: '', status: 'not_sent' }
  );
  const setField = (k, v) => setForm(f => ({ ...f, [k]: v }));

  const submit = async () => {
    setBusy(true);
    try {
      let path, body;
      if (kind === 'actions') {
        path = `/projects/${projectId}/actions`;
        body = { title: form.title, owner_id: form.owner_id,
                 due_date: form.due_date || undefined, status: form.status,
                 blocker: form.blocker || undefined };
      } else if (kind === 'approvals') {
        path = `/projects/${projectId}/client-approvals`;
        body = { item_type: form.item_type, item_title: form.item_title,
                 client_contact_name: form.client_contact_name || undefined,
                 amount: form.amount ? Number(form.amount) : undefined,
                 sent_date: form.sent_date || undefined,
                 expected_response_date: form.expected_response_date || undefined,
                 responsible_user_id: form.responsible_user_id,
                 status: form.status };
      } else {
        path = `/projects/${projectId}/collections`;
        body = { pi_number: form.pi_number,
                 total_amount: Number(form.total_amount || 0),
                 amount_received: Number(form.amount_received || 0),
                 due_date: form.due_date || undefined,
                 next_followup_date: form.next_followup_date || undefined,
                 responsible_accounts_user_id: form.responsible_accounts_user_id,
                 status: form.status };
      }
      await api.post(path, body);
      toast.success('Created');
      onDone();
    } catch (e) { toast.error(parseErr(e)); }
    finally { setBusy(false); }
  };

  const title = { actions: 'New action', approvals: 'New client approval', collections: 'New PI / collection' }[kind];
  const valid = (
    (kind === 'actions'     && form.title.trim() && form.owner_id) ||
    (kind === 'approvals'   && form.item_title.trim() && form.responsible_user_id) ||
    (kind === 'collections' && form.pi_number.trim() && Number(form.total_amount) >= 0 && form.responsible_accounts_user_id)
  );

  return (
    <Dialog open onOpenChange={(v) => !v && onClose()}>
      <DialogContent className="max-w-[480px]" data-testid={`pf-create-dialog-${kind}`}>
        <DialogHeader>
          <DialogTitle>{title}</DialogTitle>
          <DialogDescription>Logged against this project only.</DialogDescription>
        </DialogHeader>

        {kind === 'actions' && (
          <div className="space-y-2">
            <Field label="Title">
              <Input value={form.title} onChange={e => setField('title', e.target.value)}
                     placeholder="What needs to happen?" data-testid="pf-new-action-title" />
            </Field>
            <div className="grid grid-cols-2 gap-2">
              <Field label="Owner">
                <Select value={form.owner_id} onChange={v => setField('owner_id', v)}
                        options={users.map(u => ({ value: u.id, label: u.name }))}
                        placeholder="Pick owner" testId="pf-new-action-owner" />
              </Field>
              <Field label="Due date">
                <Input type="date" value={form.due_date}
                       onChange={e => setField('due_date', e.target.value)}
                       data-testid="pf-new-action-due" />
              </Field>
            </div>
          </div>
        )}

        {kind === 'approvals' && (
          <div className="space-y-2">
            <div className="grid grid-cols-2 gap-2">
              <Field label="Item type">
                <select value={form.item_type} onChange={e => setField('item_type', e.target.value)}
                        className="w-full h-9 border rounded-md px-2 text-[13px]"
                        style={{ borderColor: 'hsl(var(--border))' }}
                        data-testid="pf-new-approval-type">
                  <option value="deliverable">Deliverable</option>
                  <option value="quotation">Quotation</option>
                  <option value="proforma_invoice">Proforma invoice</option>
                </select>
              </Field>
              <Field label="Amount (₹)">
                <Input type="number" value={form.amount}
                       onChange={e => setField('amount', e.target.value)}
                       placeholder="Optional" data-testid="pf-new-approval-amount" />
              </Field>
            </div>
            <Field label="Title">
              <Input value={form.item_title} onChange={e => setField('item_title', e.target.value)}
                     placeholder="e.g. Concept deck v2, Quote #Q-208"
                     data-testid="pf-new-approval-title" />
            </Field>
            <Field label="Client contact">
              <Input value={form.client_contact_name}
                     onChange={e => setField('client_contact_name', e.target.value)}
                     placeholder="Contact name" data-testid="pf-new-approval-contact" />
            </Field>
            <div className="grid grid-cols-2 gap-2">
              <Field label="Sent date">
                <Input type="date" value={form.sent_date}
                       onChange={e => setField('sent_date', e.target.value)}
                       data-testid="pf-new-approval-sent" />
              </Field>
              <Field label="Expected reply">
                <Input type="date" value={form.expected_response_date}
                       onChange={e => setField('expected_response_date', e.target.value)}
                       data-testid="pf-new-approval-expected" />
              </Field>
            </div>
            <Field label="Responsible">
              <Select value={form.responsible_user_id}
                      onChange={v => setField('responsible_user_id', v)}
                      options={users.map(u => ({ value: u.id, label: u.name }))}
                      placeholder="Pick owner" testId="pf-new-approval-owner" />
            </Field>
          </div>
        )}

        {kind === 'collections' && (
          <div className="space-y-2">
            <div className="grid grid-cols-2 gap-2">
              <Field label="PI number">
                <Input value={form.pi_number}
                       onChange={e => setField('pi_number', e.target.value)}
                       placeholder="e.g. PI-2026-014" data-testid="pf-new-collection-pi" />
              </Field>
              <Field label="Status">
                <select value={form.status} onChange={e => setField('status', e.target.value)}
                        className="w-full h-9 border rounded-md px-2 text-[13px]"
                        style={{ borderColor: 'hsl(var(--border))' }}
                        data-testid="pf-new-collection-status">
                  {Object.entries(STATUS_LABELS.collections).map(([k, l]) =>
                    <option key={k} value={k}>{l}</option>
                  )}
                </select>
              </Field>
            </div>
            <div className="grid grid-cols-2 gap-2">
              <Field label="Total (₹)">
                <Input type="number" value={form.total_amount}
                       onChange={e => setField('total_amount', e.target.value)}
                       data-testid="pf-new-collection-total" />
              </Field>
              <Field label="Already received (₹)">
                <Input type="number" value={form.amount_received}
                       onChange={e => setField('amount_received', e.target.value)}
                       data-testid="pf-new-collection-received" />
              </Field>
            </div>
            <div className="grid grid-cols-2 gap-2">
              <Field label="Due date">
                <Input type="date" value={form.due_date}
                       onChange={e => setField('due_date', e.target.value)}
                       data-testid="pf-new-collection-due" />
              </Field>
              <Field label="Next follow-up">
                <Input type="date" value={form.next_followup_date}
                       onChange={e => setField('next_followup_date', e.target.value)}
                       data-testid="pf-new-collection-next" />
              </Field>
            </div>
            <Field label="Responsible (Accounts)">
              <Select value={form.responsible_accounts_user_id}
                      onChange={v => setField('responsible_accounts_user_id', v)}
                      options={users.map(u => ({ value: u.id, label: u.name }))}
                      placeholder="Pick accounts owner" testId="pf-new-collection-owner" />
            </Field>
          </div>
        )}

        <DialogFooter>
          <Button variant="ghost" onClick={onClose} disabled={busy}>Cancel</Button>
          <Button onClick={submit} disabled={busy || !valid}
                  data-testid={`pf-create-submit-${kind}`}>
            {busy ? 'Saving…' : 'Create'}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

function Field({ label, children }) {
  return (
    <label className="block">
      <span className="text-[11.5px] text-[hsl(var(--foreground-muted))]">{label}</span>
      <div className="mt-1">{children}</div>
    </label>
  );
}

function Select({ value, onChange, options, placeholder, testId }) {
  return (
    <select value={value || ''} onChange={e => onChange(e.target.value)}
            className="w-full h-9 border rounded-md px-2 text-[13px] bg-[hsl(var(--card))]"
            style={{ borderColor: 'hsl(var(--border))' }} data-testid={testId}>
      <option value="">{placeholder || 'Choose…'}</option>
      {options.map(o => <option key={o.value} value={o.value}>{o.label}</option>)}
    </select>
  );
}

// -------- Log dialog (mirrors global FollowupsPage semantics) --------
function LogDialog({ kind, row, onClose, onDone }) {
  const { user } = useAuth();
  const isOwner = user?.role === 'owner';
  const canReconcile = isOwner || user?.role === 'accounts';
  const [busy, setBusy] = useState(false);
  const [status, setStatus] = useState(kind === 'actions' ? 'in_progress' :
                                        kind === 'approvals' ? 'approved' : 'sent');
  const [note, setNote] = useState('');
  const [amount, setAmount] = useState('');
  const [receivedOn, setReceivedOn] = useState('');
  const [nextFollowup, setNextFollowup] = useState('');
  const [respMethod, setRespMethod] = useState('phone');
  const [respDate, setRespDate] = useState('');
  const [blocker, setBlocker] = useState('');

  const submit = async (variant) => {
    setBusy(true);
    try {
      if (kind === 'actions') {
        await api.post(`/actions/${row.id}/update`,
                       { status, note: note || undefined,
                         blocker: status === 'blocked' ? blocker : undefined });
      } else if (kind === 'approvals') {
        await api.post(`/client-approvals/${row.id}/log-response`,
                       { status, response_method: respMethod,
                         response_date: respDate || undefined, notes: note || undefined });
      } else if (kind === 'collections') {
        if (variant === 'payment') {
          if (!amount) return;
          await api.post(`/collections/${row.id}/record-payment`,
                         { amount: Number(amount), received_on: receivedOn || undefined,
                           notes: note || undefined });
        } else if (variant === 'reconcile') {
          const r = await api.post(`/collections/${row.id}/reconcile`);
          if (r.data.no_op) toast.message('Already reconciled — no change.');
          else toast.success(`Bumped project paid_amount by ₹ ${Number(r.data.delta).toLocaleString('en-IN')}`);
          onDone(); return;
        } else {
          await api.post(`/collections/${row.id}/followup`,
                         { next_followup_date: nextFollowup || undefined,
                           notes: note || undefined });
        }
      }
      toast.success('Logged');
      onDone();
    } catch (e) { toast.error(parseErr(e)); }
    finally { setBusy(false); }
  };

  const title = {
    actions:     'Log an update',
    approvals:   'Log client response',
    collections: 'Follow-up on this PI',
  }[kind];

  return (
    <Dialog open onOpenChange={(v) => !v && onClose()}>
      <DialogContent className="max-w-[440px]" data-testid={`pf-log-dialog-${kind}`}>
        <DialogHeader>
          <DialogTitle>{title}</DialogTitle>
          <DialogDescription className="truncate">
            {row.title || row.item_title || row.pi_number}
          </DialogDescription>
        </DialogHeader>

        {kind === 'actions' && (
          <div className="space-y-2">
            <Field label="Status">
              <select value={status} onChange={e => setStatus(e.target.value)}
                      className="w-full h-9 border rounded-md px-2 text-[13px]"
                      style={{ borderColor: 'hsl(var(--border))' }}
                      data-testid="pf-log-action-status">
                {Object.entries(STATUS_LABELS.actions).map(([k, l]) => <option key={k} value={k}>{l}</option>)}
              </select>
            </Field>
            {status === 'blocked' && (
              <Field label="What's blocking?">
                <Input value={blocker} onChange={e => setBlocker(e.target.value)}
                       placeholder="Client approval, budget, dependency…"
                       data-testid="pf-log-action-blocker" />
              </Field>
            )}
            <Field label="Note (optional)">
              <Textarea rows={3} value={note} onChange={e => setNote(e.target.value)}
                        placeholder="Progress, next steps, context…"
                        data-testid="pf-log-action-note" />
            </Field>
          </div>
        )}

        {kind === 'approvals' && (
          <div className="space-y-2">
            <Field label="Client response">
              <select value={status} onChange={e => setStatus(e.target.value)}
                      className="w-full h-9 border rounded-md px-2 text-[13px]"
                      style={{ borderColor: 'hsl(var(--border))' }}
                      data-testid="pf-log-approval-status">
                {Object.entries(STATUS_LABELS.approvals).map(([k, l]) => <option key={k} value={k}>{l}</option>)}
              </select>
            </Field>
            <div className="grid grid-cols-2 gap-2">
              <Field label="Via">
                <select value={respMethod} onChange={e => setRespMethod(e.target.value)}
                        className="w-full h-9 border rounded-md px-2 text-[13px]"
                        style={{ borderColor: 'hsl(var(--border))' }}
                        data-testid="pf-log-approval-method">
                  <option value="phone">Phone</option>
                  <option value="email">Email</option>
                  <option value="in_person">In person</option>
                  <option value="whatsapp">WhatsApp</option>
                  <option value="other">Other</option>
                </select>
              </Field>
              <Field label="Response date">
                <Input type="date" value={respDate}
                       onChange={e => setRespDate(e.target.value)}
                       data-testid="pf-log-approval-date" />
              </Field>
            </div>
            <Field label="Notes">
              <Textarea rows={3} value={note} onChange={e => setNote(e.target.value)}
                        placeholder="What did they say?"
                        data-testid="pf-log-approval-notes" />
            </Field>
          </div>
        )}

        {kind === 'collections' && (
          <div className="space-y-3">
            <div className="text-[12px] text-[hsl(var(--foreground-muted))]">
              Outstanding: <strong className="tabular-nums">{fmtINR(row.balance)}</strong> ·
              Total: <strong className="tabular-nums">{fmtINR(row.total_amount)}</strong>
            </div>
            <div className="border rounded-lg p-3 space-y-2" style={{ borderColor: 'hsl(var(--border))' }}>
              <div className="text-[12px] font-semibold flex items-center gap-1">
                <IndianRupee size={12} />Record a payment
              </div>
              <div className="grid grid-cols-2 gap-2">
                <Input type="number" value={amount} onChange={e => setAmount(e.target.value)}
                       placeholder="Amount" data-testid="pf-log-payment-amount" />
                <Input type="date" value={receivedOn} onChange={e => setReceivedOn(e.target.value)}
                       data-testid="pf-log-payment-date" />
              </div>
              <Input value={note} onChange={e => setNote(e.target.value)}
                     placeholder="Notes (optional)" data-testid="pf-log-payment-notes" />
              <Button size="sm" onClick={() => submit('payment')} disabled={busy || !amount}
                      data-testid="pf-log-payment-submit" className="w-full">Record payment</Button>
            </div>
            <div className="border rounded-lg p-3 space-y-2" style={{ borderColor: 'hsl(var(--border))' }}>
              <div className="text-[12px] font-semibold flex items-center gap-1">
                <PhoneCall size={12} />Log follow-up call
              </div>
              <div className="grid grid-cols-2 gap-2 items-center">
                <span className="text-[11px] text-[hsl(var(--foreground-muted))]">Next follow-up</span>
                <Input type="date" value={nextFollowup}
                       onChange={e => setNextFollowup(e.target.value)}
                       data-testid="pf-log-followup-next" />
              </div>
              <Button size="sm" variant="outline" onClick={() => submit('followup')} disabled={busy}
                      data-testid="pf-log-followup-submit" className="w-full">Save follow-up</Button>
            </div>
            {canReconcile && (
              <div className="border rounded-lg p-3 space-y-2 border-dashed"
                   style={{ borderColor: 'hsl(var(--border))' }}>
                <div className="text-[12px] font-semibold flex items-center gap-1">
                  <CheckCircle2 size={12} />Reconcile to project
                </div>
                <div className="text-[11.5px] text-[hsl(var(--foreground-muted))]">
                  Bumps the Master Project Data <em>paid_amount</em> by the unreconciled portion of this collection.
                  Idempotent — safe to click any time.
                </div>
                <Button size="sm" onClick={() => submit('reconcile')} disabled={busy}
                        data-testid="pf-log-reconcile-submit" className="w-full">Reconcile now</Button>
              </div>
            )}
          </div>
        )}

        <DialogFooter>
          <Button variant="ghost" onClick={onClose} disabled={busy}>Close</Button>
          {kind !== 'collections' && (
            <Button onClick={() => submit()} disabled={busy}
                    data-testid={`pf-log-submit-${kind}`}>
              {busy ? 'Saving…' : 'Save'}
            </Button>
          )}
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
